import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, onValue, push, set } from "firebase/database";
import { database } from "./firebaseConfig";
import { UserContext } from "./UserContext";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import {
  Copy,
  Save,
  ArrowLeft,
  Upload,
  Plus,
  Trash2,
  GripVertical,
  ArrowRightCircle,
} from "lucide-react";
import Header from "./components/header";
import Loader from "./components/loader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";
import { Label } from "./components/ui/label";

// Import @uiw/react-codemirror for text editing
import CodeMirror from "@uiw/react-codemirror";
import { markdown } from "@codemirror/lang-markdown";
import { dracula } from "@uiw/codemirror-theme-dracula";

// Import diff library for version comparison
import { diffWords } from "diff";

export default function ListViewer() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const [list, setList] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersions, setSelectedVersions] = useState({
    left: "",
    right: "",
  });
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState("csv");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [textData, setTextData] = useState("");
  const [comparisonData, setComparisonData] = useState({
    headers: [],
    diffRows: [],
    diff: [],
  });
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [newColumnName, setNewColumnName] = useState("");
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const tableRef = useRef(null);

  // Use useRef for changedCellsRefs
  const changedCellsRefs = useRef([]);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(-1);

  // Memoize handleCellChange to prevent unnecessary re-renders
  const handleCellChange = useCallback((rowIndex, header, value) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [header]: value,
      };
      return newRows;
    });
  }, []);

  useEffect(() => {
    const listRef = ref(database, `lists/${listId}`);
    const unsubscribe = onValue(listRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setList(data);
        const versionArray = Object.entries(data.history || {}).map(
          ([id, version]) => ({
            id,
            ...version,
            createdAt: new Date(version.modifiedAt),
          })
        );
        versionArray.sort((a, b) => b.createdAt - a.createdAt);
        setVersions(versionArray);
        if (versionArray.length > 1) {
          setSelectedVersions({
            left: versionArray[1].id,
            right: versionArray[0].id,
          });
        } else if (versionArray.length === 1) {
          setSelectedVersions({
            left: versionArray[0].id,
            right: versionArray[0].id,
          });
        }
        if (data.fileType) {
          setFileType(data.fileType);
        }
        if (data.fileType === "csv") {
          const dataRows = Object.values(data.data || {});
          if (dataRows.length > 0) {
            const newHeaders = Object.keys(dataRows[0]);
            setHeaders(newHeaders);
            setRows(dataRows);
            // Initialize column widths if not set
            const initialWidths = {};
            newHeaders.forEach((header) => {
              if (!columnWidths[header]) {
                initialWidths[header] = 150; // Default width
              }
            });
            setColumnWidths((prev) => ({ ...prev, ...initialWidths }));
          }
        } else {
          setTextData(data.textData || "");
        }
      } else {
        setError("No data found for this list.");
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  useEffect(() => {
    if (
      selectedVersions.left &&
      selectedVersions.right &&
      versions.length > 0
    ) {
      const leftVersion = versions.find((v) => v.id === selectedVersions.left);
      const rightVersion = versions.find((v) => v.id === selectedVersions.right);
      if (leftVersion && rightVersion) {
        if (fileType === "csv") {
          const leftRows = Object.values(leftVersion.data || {});
          const rightRows = Object.values(rightVersion.data || {});
          const headersSet = new Set([
            ...leftRows.reduce((acc, row) => acc.concat(Object.keys(row)), []),
            ...rightRows.reduce((acc, row) => acc.concat(Object.keys(row)), []),
          ]);
          const headers = Array.from(headersSet);
          // Update columnWidths for any new headers
          const initialWidths = {};
          headers.forEach((header) => {
            if (!columnWidths[header]) {
              initialWidths[header] = 150; // Default width
            }
          });
          setColumnWidths((prev) => ({ ...prev, ...initialWidths }));
          // Now we can compare the rows and create a diff at cell level
          const maxRows = Math.max(leftRows.length, rightRows.length);
          const diffRows = [];
          for (let i = 0; i < maxRows; i++) {
            const leftRow = leftRows[i] || {};
            const rightRow = rightRows[i] || {};
            const diffRow = {};
            headers.forEach((header) => {
              const leftValue = leftRow[header] || "";
              const rightValue = rightRow[header] || "";
              if (leftValue !== rightValue) {
                diffRow[header] = {
                  left: leftValue,
                  right: rightValue,
                  changed: true,
                };
              } else {
                diffRow[header] = {
                  value: leftValue,
                  changed: false,
                };
              }
            });
            diffRows.push(diffRow);
          }
          setComparisonData({ headers, diffRows });
        } else {
          const oldText = leftVersion.textData || "";
          const newText = rightVersion.textData || "";
          const diff = diffWords(oldText, newText);
          setComparisonData({ diff });
        }
        // Reset current change index and clear refs
        setCurrentChangeIndex(-1);
        changedCellsRefs.current = [];
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersions, versions, fileType]);

  const handleResizeMouseDown = (e, header) => {
    e.preventDefault();
    setResizingColumn(header);
    setStartX(e.pageX);
    setStartWidth(columnWidths[header] || 150);
  };

  const handleResizeMouseMove = (e) => {
    if (resizingColumn) {
      e.preventDefault();
      const diff = e.pageX - startX;
      const newWidth = Math.max(100, startWidth + diff); // Minimum width of 100px
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth,
      }));
    }
  };

  const handleResizeMouseUp = () => {
    setResizingColumn(null);
  };

  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener("mousemove", handleResizeMouseMove);
      document.addEventListener("mouseup", handleResizeMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleResizeMouseMove);
        document.removeEventListener("mouseup", handleResizeMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizingColumn]);

  const handleUpload = () => {
    if (uploadFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const fileName = uploadFile.name;
        const fileExtension = fileName.split(".").pop().toLowerCase();

        if (fileExtension === "csv") {
          setFileType("csv");
          const lines = content
            .split(/\r?\n/)
            .filter((line) => line.trim());
          const csvHeaders = lines[0].split(",").map((h) => h.trim());
          const csvRows = lines.slice(1).map((line) => {
            const values = line.split(",");
            return csvHeaders.reduce((obj, header, index) => {
              obj[header] = values[index] ? values[index].trim() : "";
              return obj;
            }, {});
          });

          setHeaders(csvHeaders);
          setRows(csvRows);
          // Initialize column widths for new headers
          const initialWidths = {};
          csvHeaders.forEach((header) => {
            if (!columnWidths[header]) {
              initialWidths[header] = 150; // Default width
            }
          });
          setColumnWidths((prev) => ({ ...prev, ...initialWidths }));
        } else {
          setFileType("text");
          setTextData(content);
        }
        setIsUploadDialogOpen(false);
      };
      reader.readAsText(uploadFile);
    }
  };

  const handleSave = () => {
    const modifiedAt = Date.now();
    const modifiedBy = currentUser.uid;

    if (fileType === "csv") {
      const newData = rows.reduce((acc, row, index) => {
        acc[index] = row;
        return acc;
      }, {});

      const newVersion = {
        data: newData,
        modifiedBy,
        modifiedAt,
        fileType,
      };

      push(ref(database, `lists/${listId}/history`), newVersion)
        .then(() => {
          set(ref(database, `lists/${listId}/data`), newData);
          set(ref(database, `lists/${listId}/fileType`), fileType);
          alert("Changes saved successfully.");
        })
        .catch((error) => {
          console.error("Error saving changes:", error);
          alert("Failed to save changes. Please try again.");
        });
    } else {
      const newVersion = {
        textData,
        modifiedBy,
        modifiedAt,
        fileType,
      };

      push(ref(database, `lists/${listId}/history`), newVersion)
        .then(() => {
          set(ref(database, `lists/${listId}/textData`), textData);
          set(ref(database, `lists/${listId}/fileType`), fileType);
          alert("Changes saved successfully.");
        })
        .catch((error) => {
          console.error("Error saving changes:", error);
          alert("Failed to save changes. Please try again.");
        });
    }
  };

  const handleCopy = () => {
    if (fileType === "csv") {
      const csvContent = [headers.join(",")];
      rows.forEach((row) => {
        const rowData = headers.map((header) => row[header] || "");
        csvContent.push(rowData.join(","));
      });
      const csvString = csvContent.join("\n");
      navigator.clipboard
        .writeText(csvString)
        .then(() => {
          alert("CSV data copied to clipboard!");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          alert("Failed to copy CSV data. Please try again.");
        });
    } else {
      navigator.clipboard
        .writeText(textData)
        .then(() => {
          alert("Text data copied to clipboard!");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          alert("Failed to copy text data. Please try again.");
        });
    }
  };

  const handleBack = () => navigate("/dashboard");

  const handleAddRow = () => {
    const newRow = headers.reduce((acc, header) => {
      acc[header] = "";
      return acc;
    }, {});
    setRows([...rows, newRow]);
  };

  const handleDeleteRow = (rowIndex) => {
    const newRows = rows.filter((_, index) => index !== rowIndex);
    setRows(newRows);
  };

  const handleAddColumn = () => {
    if (!newColumnName.trim()) {
      alert("Please enter a column name");
      return;
    }
    const columnName = newColumnName.trim();
    if (headers.includes(columnName)) {
      alert("Column already exists");
      return;
    }
    setHeaders([...headers, columnName]);
    setRows(
      rows.map((row) => ({
        ...row,
        [columnName]: "",
      }))
    );
    setColumnWidths((prev) => ({
      ...prev,
      [columnName]: 150, // Default width for new column
    }));
    setNewColumnName("");
  };

  const handleDeleteColumn = (header) => {
    const newHeaders = headers.filter((h) => h !== header);
    const newRows = rows.map((row) => {
      const newRow = { ...row };
      delete newRow[header];
      return newRow;
    });
    setHeaders(newHeaders);
    setRows(newRows);
    const newColumnWidths = { ...columnWidths };
    delete newColumnWidths[header];
    setColumnWidths(newColumnWidths);
  };

  // Function to handle jumping to next change
  const handleNextChange = () => {
    if (changedCellsRefs.current.length === 0) return;
    const nextIndex =
      (currentChangeIndex + 1) % changedCellsRefs.current.length;
    setCurrentChangeIndex(nextIndex);
    const cellRef = changedCellsRefs.current[nextIndex];
    if (cellRef && cellRef.current) {
      cellRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
      // Optionally, highlight the cell
      cellRef.current.classList.add("highlighted-cell");
      // Remove highlight after a short delay
      setTimeout(() => {
        if (cellRef.current) {
          cellRef.current.classList.remove("highlighted-cell");
        }
      }, 2000);
    }
  };

  function TextDiffViewer({ diff }) {
    return (
      <pre className="bg-gray-800 p-4 rounded text-gray-100 whitespace-pre-wrap">
        {diff.map((part, index) => {
          const color = part.added
            ? "bg-green-900/50 text-green-400"
            : part.removed
            ? "bg-red-900/50 text-red-400"
            : "text-gray-100";
          return (
            <span key={index} className={color}>
              {part.value}
            </span>
          );
        })}
      </pre>
    );
  }

  // Define the EditableCell component
  const EditableCell = React.memo(function EditableCell({
    rowIndex,
    header,
    value,
    onChange,
    width,
  }) {
    const [cellValue, setCellValue] = useState(value || "");
    const inputRef = useRef(null);

    // Update local state when the value prop changes and the input is not focused
    useEffect(() => {
      if (document.activeElement !== inputRef.current) {
        setCellValue(value || "");
      }
    }, [value]);

    const handleInputChange = (e) => {
      setCellValue(e.target.value);
    };

    const handleBlur = () => {
      onChange(rowIndex, header, cellValue);
    };

    return (
      <input
        ref={inputRef}
        type="text"
        value={cellValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        className="w-full bg-transparent border-0 focus:outline-none p-0 text-gray-100"
        style={{
          minWidth: `${width}px`,
          width: `${width}px`,
          wordBreak: "break-word",
        }}
      />
    );
  });

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (!list) {
    return <Loader isFullScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="border-gray-300 text-gray-300 hover:bg-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="bg-gray-800 rounded-xl shadow-2xl overflow-hidden mb-8">
          <div className="p-6">
            <h2 className="text-2xl font-semibold mb-4 text-green-400">
              {list.name}
            </h2>

            <div className="mb-6">
              {fileType === "csv" ? (
                <div>
                  <div className="mb-4 flex gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddRow}
                      className="border-gray-300 text-gray-300 hover:bg-gray-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Row
                    </Button>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="New column name"
                        value={newColumnName}
                        onChange={(e) => setNewColumnName(e.target.value)}
                        className="bg-gray-700 text-gray-100 border-gray-600"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddColumn}
                        className="border-gray-300 text-gray-300 hover:bg-gray-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Column
                      </Button>
                    </div>
                  </div>
                  <div
                    className="table-scroll-container"
                    style={{ height: "70vh" }}
                  >
                    <div
                      className="table-container"
                      style={{ height: "100%", overflow: "auto" }}
                    >
                      <table className="w-full" ref={tableRef}>
                        <thead className="bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <th className="w-12 px-2 py-2 sticky left-0 bg-gray-700 z-20"></th>
                            {headers.map((header) => (
                              <th
                                key={header}
                                className="text-left text-green-400 font-semibold relative group"
                                style={{
                                  minWidth: `${columnWidths[header]}px`,
                                  width: `${columnWidths[header]}px`,
                                }}
                              >
                                <div className="flex items-center px-4 py-2">
                                  <span className="flex-grow">{header}</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteColumn(header)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-400" />
                                  </Button>
                                  <div
                                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-green-400"
                                    onMouseDown={(e) =>
                                      handleResizeMouseDown(e, header)
                                    }
                                  >
                                    <GripVertical className="w-4 h-4 absolute top-1/2 -translate-y-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100" />
                                  </div>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, rowIndex) => (
                            <tr
                              key={rowIndex}
                              className="border-t border-gray-700 hover:bg-gray-700/50 group"
                            >
                              <td className="w-12 px-2 py-2 sticky left-0 bg-gray-800 group-hover:bg-gray-700/50 z-10">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </Button>
                              </td>
                              {headers.map((header) => (
                                <td
                                  key={`${rowIndex}-${header}`}
                                  className="px-4 py-2 align-top"
                                  style={{
                                    minWidth: `${columnWidths[header]}px`,
                                    width: `${columnWidths[header]}px`,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  <EditableCell
                                    rowIndex={rowIndex}
                                    header={header}
                                    value={row[header] || ""}
                                    onChange={handleCellChange}
                                    width={columnWidths[header]}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <CodeMirror
                  value={textData}
                  extensions={[markdown()]}
                  theme={dracula}
                  onChange={(value) => setTextData(value)}
                  basicSetup={{
                    lineNumbers: true,
                    highlightActiveLine: true,
                  }}
                  editable={true}
                />
              )}
            </div>

            <div className="flex justify-between items-center mb-4">
              <div>
                <Dialog
                  open={isUploadDialogOpen}
                  onOpenChange={setIsUploadDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-gray-300 text-gray-300 hover:bg-gray-700"
                      as="span"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload File
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-800 text-gray-100">
                    <DialogHeader>
                      <DialogTitle className="text-green-400">
                        Upload a File
                      </DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="list-file" className="text-right">
                          File
                        </Label>
                        <Input
                          id="list-file"
                          type="file"
                          accept=".csv,.txt,.md"
                          className="col-span-3 bg-gray-700 text-gray-100 border-gray-600"
                          onChange={(e) => setUploadFile(e.target.files[0])}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        onClick={handleUpload}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        Upload
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSave}
                  className="border-gray-300 text-gray-300 hover:bg-gray-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="border-gray-300 text-gray-300 hover:bg-gray-700"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4 text-green-400">
                Version Comparison
              </h3>
              <div className="flex justify-between mb-4 items-center">
                <div className="flex space-x-4">
                  <select
                    value={selectedVersions.left}
                    onChange={(e) =>
                      setSelectedVersions((prev) => ({
                        ...prev,
                        left: e.target.value,
                      }))
                    }
                    className="bg-gray-700 text-gray-100 border border-gray-600 rounded p-2"
                  >
                    {versions.map((version) => (
                      <option key={version.id} value={version.id}>
                        {version.createdAt.toLocaleString()}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedVersions.right}
                    onChange={(e) =>
                      setSelectedVersions((prev) => ({
                        ...prev,
                        right: e.target.value,
                      }))
                    }
                    className="bg-gray-700 text-gray-100 border border-gray-600 rounded p-2"
                  >
                    {versions.map((version) => (
                      <option key={version.id} value={version.id}>
                        {version.createdAt.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>
                {fileType === "csv" &&
                  comparisonData.headers.length > 0 &&
                  comparisonData.diffRows.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextChange}
                      className="border-gray-300 text-gray-300 hover:bg-gray-700"
                    >
                      <ArrowRightCircle className="w-4 h-4 mr-2" />
                      Next Change
                    </Button>
                  )}
              </div>
              {fileType === "csv" ? (
                comparisonData.headers.length > 0 &&
                comparisonData.diffRows.length > 0 ? (
                  <div
                    className="table-scroll-container"
                    style={{ height: "70vh" }}
                  >
                    <div
                      className="table-container"
                      style={{ height: "100%", overflow: "auto" }}
                    >
                      <table className="w-full">
                        <thead className="bg-gray-700 sticky top-0 z-10">
                          <tr>
                            {comparisonData.headers.map((header) => (
                              <th
                                key={header}
                                className="text-left text-green-400 font-semibold relative group"
                                style={{
                                  minWidth: `${columnWidths[header]}px`,
                                  width: `${columnWidths[header]}px`,
                                }}
                              >
                                <div className="flex items-center px-4 py-2">
                                  <span className="flex-grow">{header}</span>
                                  <div
                                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-green-400"
                                    onMouseDown={(e) =>
                                      handleResizeMouseDown(e, header)
                                    }
                                  >
                                    <GripVertical className="w-4 h-4 absolute top-1/2 -translate-y-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100" />
                                  </div>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Clear refs before collecting new ones */}
                          {(() => {
                            changedCellsRefs.current = [];
                            return null;
                          })()}
                          {comparisonData.diffRows.map((row, rowIndex) => (
                            <tr
                              key={rowIndex}
                              className="border-t border-gray-700"
                            >
                              {comparisonData.headers.map((header) => {
                                const cellData = row[header];
                                if (cellData.changed) {
                                  // Create a ref for this cell
                                  const cellRef = React.createRef();
                                  // Add it to the refs array
                                  changedCellsRefs.current.push(cellRef);

                                  return (
                                    <td
                                      key={`${rowIndex}-${header}`}
                                      ref={cellRef}
                                      className="px-4 py-2 bg-red-900/50 align-top"
                                      style={{
                                        minWidth: `${columnWidths[header]}px`,
                                        width: `${columnWidths[header]}px`,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      <div>
                                        <div className="text-red-400 line-through">
                                          {cellData.left || ""}
                                        </div>
                                        <div className="text-green-400">
                                          {cellData.right || ""}
                                        </div>
                                      </div>
                                    </td>
                                  );
                                } else {
                                  return (
                                    <td
                                      key={`${rowIndex}-${header}`}
                                      className="px-4 py-2 align-top"
                                      style={{
                                        minWidth: `${columnWidths[header]}px`,
                                        width: `${columnWidths[header]}px`,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {cellData.value || ""}
                                    </td>
                                  );
                                }
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400">No differences to display.</p>
                )
              ) : (
                <div className="overflow-x-auto">
                  {comparisonData.diff && comparisonData.diff.length === 0 ? (
                    <p className="text-gray-400">No differences to display.</p>
                  ) : (
                    <TextDiffViewer diff={comparisonData.diff} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
